import './ServiceOrderForm.scss';

import { Form, FormProps, message } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FormSection } from '@/components/general/FormSection';
import { createFinishFailedHandler } from '@/utils/formHelpers';

import ServiceOrderServiceDetail from './ServiceOrderServiceDetail';
import useServiceOrderForm from './useServiceOrderForm';

interface ServiceOrderFormProps {
  formProps: FormProps;
}

// Usado únicamente por ServiceOrderCreate: una orden nueva siempre arranca en
// PENDIENTE_DE_COORDINACION, así que solo "Detalles del servicio" aplica acá
// (facturación/items/administración/Nota Fiscal se editan después desde el Show).
const ServiceOrderForm = (props: ServiceOrderFormProps) => {
  const { t } = useTranslation();
  const {
    formInitialValues,
    technicianSelectProps,
    companySelectProps,
    ticketsSelectProps,
    ticketMachineOptions,
    ticketMachineOptionsLoading,
    companyContactsSelectProps,
    companyContactRules,
    clearDraft,
    createdAutomatically,
  } = useServiceOrderForm(props.formProps);
  const initialTickets = useMemo(
    () => props.formProps?.initialValues?.tickets.map(ticket => ticket.id) ?? [],
    [props.formProps?.initialValues?.tickets],
  );
  const formProps = useMemo(
    () => ({ ...props.formProps, initialValues: formInitialValues }),
    [props.formProps, formInitialValues],
  );
  const selectedCompany = Form.useWatch('company', formProps.form);
  // Handler estabilizado para evitar re-render del Form en cada render del padre.
  const handleFinishFailed = useMemo(() => createFinishFailedHandler(t), [t]);
  const handleFinish = useCallback(
    (v: any) => {
      if (!v.moviles || v.moviles.length === 0) {
        message.error(
          t('El ticket asociado a la orden de servicio debe tener al menos un móvil asignado'),
        );

        return;
      }

      clearDraft();

      formProps.onFinish?.(v);
    },
    [t, formProps, clearDraft],
  );

  return (
    <Form
      {...formProps}
      className="ServiceOrderForm"
      layout="vertical"
      onFinishFailed={handleFinishFailed}
      onFinish={handleFinish}>
      <FormSection title={t('Detalles del servicio')}>
        <ServiceOrderServiceDetail
          formProps={formProps}
          selectedCompany={selectedCompany}
          technicianSelectProps={technicianSelectProps}
          companySelectProps={companySelectProps}
          ticketsSelectProps={ticketsSelectProps}
          initialTickets={initialTickets}
          ticketMachineOptions={ticketMachineOptions}
          ticketMachineOptionsLoading={ticketMachineOptionsLoading}
          companyContactsSelectProps={companyContactsSelectProps}
          companyContactRules={companyContactRules}
          createdAutomatically={createdAutomatically}
        />
      </FormSection>
    </Form>
  );
};

export default ServiceOrderForm;
